import React, { useCallback, useMemo } from 'react';
import { Accordion } from 'react-bootstrap';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SimpleCheckbox from '../../common/SimpleCheckbox';
import RadioGroup from '../../common/RadioGroup';
import Radio from '../../common/Radio';
import * as hash from 'object-hash';
import { ApprovedStatus } from '../../../constants/approvedStatus';
import styles from './ProductFilter.module.scss';
import { ActiveStatus, ActiveStatusLabel } from '../../../constants/activeStatus';
import { isArray, upperFirst } from 'lodash';
import { StaticFilterKeys, StaticFilters } from '../../../constants/staticFilters';

function ProductFilter(props) {
  const { currentFilters, setFilterSearch, setFilter } = props;

  const searchChangeHandler = (key) => (e) => {
    setFilterSearch(key, e.target.value);
  };

  const clearAllFilters = () => {
    setFilter({});
  };

  const isFilteringArchived = useMemo(() => {
    return currentFilters?.activeProducts === ActiveStatus.ARCHIVED;
  }, [currentFilters]);

  const clearFilter = useCallback(
    (key) => {
      const newFilter = {
        ...currentFilters,
      };
      delete newFilter[key];

      setFilterSearch(key, '');
      setFilter(newFilter);
    },
    [setFilterSearch, setFilter, currentFilters],
  );

  const handleCheck = useCallback(
    (key, value) => {
      const index = currentFilters[key] ? currentFilters[key].indexOf(value) : -1;
      const newFilter = { ...currentFilters };

      if (index > -1) {
        const oldItems = [...currentFilters[key]];
        oldItems.splice(index, 1);

        if (oldItems.length) {
          newFilter[key] = oldItems;
        } else {
          delete newFilter[key];
        }
      } else {
        const newItems = currentFilters[key] ? [...currentFilters[key]] : [];
        newItems.push(value);
        newFilter[key] = newItems;
      }

      setFilter(newFilter);
    },
    [setFilter, currentFilters],
  );

  const handleRadio = (key, value) => {
    const newFilter = { ...props.currentFilters };
    newFilter[key] = value;
    props.setFilter(newFilter);
  };

  const isChecked = useCallback((key, item) => !!currentFilters?.[key]?.includes(item), [currentFilters]);

  const hasChecked = useCallback(
    (key) => {
      if (!currentFilters?.[key]) return false;

      if (isArray(currentFilters[key])) {
        return currentFilters[key].length > 0;
      }

      return key === StaticFilters.ACTIVE_PRODUCTS ? currentFilters[key] !== ActiveStatus.ACTIVE : false;
    },
    [currentFilters],
  );

  const hasActiveFilters = useCallback(() => {
    const { activeProducts, ...filters } = currentFilters;
    const hasActiveProducts = activeProducts !== undefined && activeProducts !== ActiveStatus.ACTIVE;
    return Object.keys(filters).length > 0 || hasActiveProducts;
  }, [currentFilters]);

  const clearAllButton = () => {
    if (hasActiveFilters()) {
      return (
        <div className={styles.clear}>
          <span onClick={clearAllFilters}>
            Clear all filters
            <span className={cn(styles['ic-close'], 'ic-close')} />
          </span>
        </div>
      );
    }
  };

  const renderFilterSection = useCallback(
    (key, name, content) => {
      if (isFilteringArchived && key !== StaticFilters.ACTIVE_PRODUCTS) {
        return;
      }
      if (key === StaticFilters.IS_RETAILER_APPROVED && props.connectionHasAutoSync) {
        return;
      }

      return (
        <Accordion>
          <Accordion.Header>
            <div className={cn(styles.section, hasChecked(key) ? styles.hasChecked : '')}>
              <span>{name}</span>
              <span
                className={cn(styles.clear, 'clear')}
                onClick={(e) => {
                  clearFilter(key);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                Clear
              </span>
            </div>
          </Accordion.Header>
          <Accordion.Body>{content}</Accordion.Body>
        </Accordion>
      );
    },
    [isFilteringArchived, clearFilter, hasChecked, props.connectionHasAutoSync],
  );

  const renderFilter = (name, key) => {
    const { filters, currentFilterSearches } = props;

    if (!filters?.[key]) {
      return;
    }
    const search = (currentFilterSearches[key] || '').toLowerCase();
    const keys = Object.keys(filters[key]);

    return renderFilterSection(
      key,
      name,
      <>
        <div className="search">
          <input type="text" value={currentFilterSearches[key]} onChange={searchChangeHandler(key)} />
          <div className="ic-search" />
        </div>

        <div className={styles.filters_group} key={hash(filters)}>
          {keys.map((filterKey) => (
            <SimpleCheckbox
              key={filterKey}
              value={filterKey}
              label={filterKey}
              hidden={!filterKey.toLowerCase().includes(search)}
              counter={props.showCount ? filters[key][filterKey] : null}
              onChange={(checked) => handleCheck(key, filterKey)}
              checked={isChecked(key, filterKey)}
            />
          ))}
        </div>
      </>,
    );
  };

  const renderFilters = () => {
    return Object.entries(props.filters).map((x) => {
      const key = x[0];
      const name = upperFirst(key);

      return (
        <Accordion.Item key={key} eventKey={key}>
          {renderFilter(name, key)}
        </Accordion.Item>
      );
    });
  };

  const renderStaticFilter = (key) => {
    const section = renderFilterSection(
      key,
      StaticFilterKeys[key],
      <div className={styles.filters_group}>
        <SimpleCheckbox
          id={`${key}Yes`}
          value="Yes"
          label="Yes"
          onChange={(checked) => handleCheck(key, 'Yes')}
          checked={isChecked(key, 'Yes')}
        />
        <SimpleCheckbox
          id={`${key}No`}
          value="No"
          label="No"
          onChange={(checked) => handleCheck(key, 'No')}
          checked={isChecked(key, 'No')}
        />
      </div>,
    );

    return (
      <Accordion.Item key={key} eventKey={key}>
        {section}
      </Accordion.Item>
    );
  };

  const renderApprovedStatus = () => {
    const key = StaticFilters.APPROVED_STATUS;

    const section = renderFilterSection(
      key,
      'Status',
      <div className={styles.filters_group}>
        <RadioGroup
          name={key}
          column
          selectedvalue={currentFilters[key] || ''}
          onChange={(value) => handleRadio(key, value)}
        >
          <Radio id={`${key}_all`} value="" label="All" />
          <Radio id={ApprovedStatus.NOT_APPROVED} value={ApprovedStatus.NOT_APPROVED} label="Not Approved" />
          <Radio id={ApprovedStatus.PENDING} value={ApprovedStatus.PENDING} label="Pending" />
          <Radio id={ApprovedStatus.SYNCED} value={ApprovedStatus.SYNCED} label="Synced" />
        </RadioGroup>
      </div>,
    );

    return (
      <Accordion.Item key={key} eventKey={key}>
        {section}
      </Accordion.Item>
    );
  };

  const renderActiveProducts = () => {
    const key = StaticFilters.ACTIVE_PRODUCTS;

    const section = renderFilterSection(
      key,
      StaticFilterKeys[StaticFilters.ACTIVE_PRODUCTS],
      <div className={styles.filters_group}>
        <RadioGroup
          name={key}
          column
          selectedvalue={props.currentFilters[key] || ActiveStatus.ACTIVE}
          onChange={(value) => handleRadio(key, value)}
        >
          {Object.keys(ActiveStatus).map((status) => (
            <Radio key={status} id={status} value={status} label={ActiveStatusLabel[status]} />
          ))}
        </RadioGroup>
      </div>,
    );

    return (
      <Accordion.Item key={key} eventKey={key}>
        {section}
      </Accordion.Item>
    );
  };

  const renderStaticFilters = () => {
    const { staticFilters } = props;

    if (staticFilters?.length) {
      return Object.keys(StaticFilterKeys).reduce((filters, filter) => {
        if (staticFilters.includes(filter)) {
          if (filter === StaticFilters.APPROVED_STATUS) {
            filters.push(renderApprovedStatus());
          } else if (filter === StaticFilters.ACTIVE_PRODUCTS) {
            filters.push(renderActiveProducts());
          } else {
            filters.push(renderStaticFilter(filter));
          }
        }

        return filters;
      }, []);
    }
  };

  return (
    <>
      <div className={styles.filter}>
        Filter <span>{clearAllButton()}</span>
      </div>

      <Accordion className={styles.accordion}>
        {renderStaticFilters()}
        {renderFilters()}
      </Accordion>
    </>
  );
}

export default ProductFilter;

ProductFilter.defaultProps = {
  staticFilters: null,
  showCount: true,
};

ProductFilter.propTypes = {
  filters: PropTypes.object.isRequired,
  staticFilters: PropTypes.array,
  currentFilters: PropTypes.object.isRequired,
  currentFilterSearches: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  setFilterSearch: PropTypes.func,
  showCount: PropTypes.bool,
};
